import { memo, useEffect, useId } from 'react'
import { formatInches } from '../../../lib/framingDimensions'
import useFramingPreviewImageStore from '../../../stores/useFramingPreviewImageStore'
import styles from './FramingPreview.module.css'
import FrameBorder from './FrameBorder'

// shortcut: use a proportional paper border until production border measurements are available.
const DECKLED_BORDER_RATIO = 0.04
const FRAME_SEAM_BLEED = 0.02

const PreviewShape = ({ shape, rect, ...props }) => {
  if (shape === 'circle') {
    return (
      <circle
        cx={rect.x + rect.width / 2}
        cy={rect.y + rect.height / 2}
        r={Math.min(rect.width, rect.height) / 2}
        {...props}
      />
    )
  }
  if (shape === 'oval') {
    return (
      <ellipse
        cx={rect.x + rect.width / 2}
        cy={rect.y + rect.height / 2}
        rx={rect.width / 2}
        ry={rect.height / 2}
        {...props}
      />
    )
  }
  return <rect {...rect} {...props} />
}

const Artwork = ({ artwork, image, rect }) => {
  if (image.status !== 'ready') return <rect {...rect} fill="#f4f4f4" />
  const { crop } = artwork
  if (!crop || artwork.preCropped) {
    return (
      <image
        {...rect}
        href={artwork.url}
        preserveAspectRatio="xMidYMid slice"
      />
    )
  }
  const viewBox = [
    (crop.x * image.width) / 100,
    (crop.y * image.height) / 100,
    (crop.width * image.width) / 100,
    (crop.height * image.height) / 100,
  ].join(' ')
  return (
    <svg
      {...rect}
      viewBox={viewBox}
      preserveAspectRatio="xMidYMid slice"
      overflow="hidden"
    >
      <image href={artwork.url} width={image.width} height={image.height} />
    </svg>
  )
}

const FramingPreview = ({ config, imagesPreloaded = false }) => {
  const id = `framing-${useId().replace(/:/g, '')}`
  const ready = config?.status === 'ready'
  const isFloatCanvas = config?.construction === 'float-canvas'
  const isCanvas = isFloatCanvas || config?.construction === 'canvas'
  const textureUrl = ready ? config.frame?.assetUrl : null
  const photoUrl = ready ? config.artwork?.url : null
  const loadPreviewImage = useFramingPreviewImageStore((state) => state.loadPreviewImage)
  const textureEntry = useFramingPreviewImageStore((state) =>
    textureUrl ? state.previewImages[textureUrl] : undefined,
  )
  const photoEntry = useFramingPreviewImageStore((state) =>
    photoUrl ? state.previewImages[photoUrl] : undefined,
  )
  const texture = textureEntry || { status: textureUrl ? 'loading' : 'empty' }
  const photo = photoEntry || { status: photoUrl ? 'loading' : 'empty' }

  useEffect(() => {
    if (imagesPreloaded) return
    loadPreviewImage(textureUrl)
    loadPreviewImage(photoUrl)
  }, [imagesPreloaded, textureUrl, photoUrl, loadPreviewImage])

  const dimensions = ready ? config.dimensions : null
  const artworkBleed = ready && isCanvas && !isFloatCanvas &&
    Object.values(dimensions.mat).every((value) => value === 0)
    ? FRAME_SEAM_BLEED
    : 0
  const artworkRect = dimensions
    ? {
        x: dimensions.artwork.x - artworkBleed,
        y: dimensions.artwork.y - artworkBleed,
        width: dimensions.artwork.width + artworkBleed * 2,
        height: dimensions.artwork.height + artworkBleed * 2,
      }
    : null
  const matOpeningShape = ready ? config.matOpeningShape || 'standard' : 'standard'
  const paperShape = ready ? config.paper?.shape || 'standard' : 'standard'
  const borderInset = ready && config.paper?.hasBorder
    ? Math.min(dimensions.artwork.width, dimensions.artwork.height) * DECKLED_BORDER_RATIO
    : 0
  const photoRect = dimensions
    ? {
        x: artworkRect.x + borderInset,
        y: artworkRect.y + borderInset,
        width: artworkRect.width - borderInset * 2,
        height: artworkRect.height - borderInset * 2,
      }
    : null
  const textureReady = ready && texture.status === 'ready'
  let message = config?.message
  if (ready && !config.frame?.assetUrl) message = 'Preview unavailable: the selected frame has no texture image.'
  else if (ready && texture.status === 'error') message = 'The frame image could not be loaded. Your order selections are unchanged.'
  else if (ready && texture.status === 'loading') message = 'Loading frame preview…'
  else if (textureReady && photo.status === 'error') message = 'The photograph could not be loaded. The frame dimensions are still shown.'
  else if (textureReady && photo.status === 'loading') message = 'Loading photograph…'
  else if (textureReady && photo.status === 'empty') message = 'Select a photograph to see it inside the frame.'

  return (
    <section className={styles.panel} aria-labelledby={`${id}-heading`}>
      <h3 id={`${id}-heading`} className={styles.heading}>
        Framing preview
      </h3>
      {textureReady && (
        <>
          <svg
            className={styles.preview}
            viewBox={`0 0 ${dimensions.outerWidth} ${dimensions.outerHeight}`}
            preserveAspectRatio="xMidYMid meet"
            role="img"
            aria-labelledby={`${id}-title ${id}-description`}
          >
            <title id={`${id}-title`}>{config.frame.name} framing preview</title>
            <desc id={`${id}-description`}>
              {config.artwork.name}. {isCanvas ? 'Canvas' : 'Print'} {formatInches(dimensions.printWidth)} by{' '}
              {formatInches(dimensions.printHeight)} inches. {isFloatCanvas ? 'Approximate' : 'Finished'} outside dimensions{' '}
              {formatInches(dimensions.outerWidth)} by {formatInches(dimensions.outerHeight)}{' '}
              inches.
            </desc>
            <defs>
              <clipPath id={`${id}-artwork`}>
                <PreviewShape shape={matOpeningShape} rect={artworkRect} />
              </clipPath>
              <clipPath id={`${id}-paper`}>
                <PreviewShape shape={paperShape} rect={photoRect} />
              </clipPath>
            </defs>
            <FrameBorder id={id} frame={config.frame} dimensions={dimensions} texture={texture}>
              <rect {...dimensions.mount} fill={config.matColor} />
              <g clipPath={`url(#${id}-artwork)`}>
                {config.paper?.hasBorder && (
                  <PreviewShape shape={paperShape} rect={dimensions.artwork} fill="#ffffff" />
                )}
                <g clipPath={`url(#${id}-paper)`}>
                  <Artwork
                    artwork={config.artwork}
                    image={photo}
                    rect={photoRect}
                  />
                </g>
              </g>
            </FrameBorder>
          </svg>
          <p className={styles.dimensions}>
            {isCanvas ? 'Canvas' : 'Print'}: {formatInches(dimensions.printWidth)} × {formatInches(dimensions.printHeight)} in
            {(!isCanvas || isFloatCanvas) && (
              <span>
                {isFloatCanvas ? (
                  <>Float gap: {formatInches(config.floatGapInches)} in (preview)</>
                ) : (
                  <>
                    Mat outside: {formatInches(dimensions.matWidth)} ×{' '}
                    {formatInches(dimensions.matHeight)} in
                  </>
                )}
              </span>
            )}
            <strong>
              {isFloatCanvas ? 'Approximate outside' : 'Finished outside'}: {formatInches(dimensions.outerWidth)} ×{' '}
              {formatInches(dimensions.outerHeight)} in
            </strong>
          </p>
          {config.batch && (
            <p className={styles.message}>
              The preview shows the selected photograph. Individual crops are not applied when
              adding all photos.
            </p>
          )}
        </>
      )}
      {message && (
        <p className={styles.message} role="status">
          {message}
        </p>
      )}
    </section>
  )
}

export default memo(FramingPreview)
